import cookie from 'react-cookie';
import moment from 'moment';
import config from '~/config';
import ErrorService from '~/utils/ErrorService';
import ApiService from './ApiService';
import { SOURCECLEAR_COMPONENTS } from '~/constants/ModelConstants';

const BEARER_TOKEN_KEY = 'bearer-token';
const SESSION_COOKIE_KEY = 'session-token';

const AuthService = function() {
  const setSessionToken = (sessionToken, expiresIn = 30 * 60) => {
    const isSecure = !!config.BEARER_DOMAINS.find(domain =>
      window.location.hostname.endsWith(domain)
    );
    config.BEARER_DOMAINS.forEach(function(domain) {
      cookie.save(SESSION_COOKIE_KEY, sessionToken, {
        expires: moment()
          .add(expiresIn, 'second')
          .toDate(),
        domain: domain,
        secure: isSecure,
        path: '/' + SOURCECLEAR_COMPONENTS.VULNERABILITY_DATABASE.slug,
      });
    });
  };

  const clearSessionToken = () => {
    config.BEARER_DOMAINS.forEach(function(domain) {
      // flush the old JWT if it still exists
      cookie.remove(BEARER_TOKEN_KEY, {
        domain: domain,
      });
      cookie.remove(SESSION_COOKIE_KEY, {
        domain: domain,
        path: '/' + SOURCECLEAR_COMPONENTS.VULNERABILITY_DATABASE.slug,
      });
    });
  };

  /**
   * Sets bearer token for user with an expiry time.
   * @param {String} authToken the API token for the current session, or <tt>null</tt> to <b>CLEAR</b> the session.
   */
  const setAuthToken = authToken => {
    return new Promise(resolve => {
      if (authToken) {
        setSessionToken(authToken);
      } else {
        clearSessionToken();
      }
      resolve();
    });
  };

  /**
   * Sets bearer token for user with an expiry time.
   * @param {String} authToken the API token for the current session, or <tt>null</tt> to <b>CLEAR</b> the session.
   */
  const resetAuthToken = authToken => {
    clearSessionToken();
    setSessionToken(authToken);
  };

  const logout = () => {
    const tokenHeader = getAuthToken();
    return ApiService.post('/logout', { headers: { 'x-auth-token': tokenHeader } })
      .then(
        () => {
          setAuthToken(null);
        },
        error => {
          ErrorService.capture('Error logging user out', error);
          setAuthToken(null);
        }
      )
      .catch(error => {
        ErrorService.capture('Error logging user out', error);
        setAuthToken(null);
      });
  };

  const getAuthToken = () => {
    return cookie.load(SESSION_COOKIE_KEY);
  };

  return {
    getAuthToken,
    setAuthToken,
    logout,
    resetAuthToken,
  };
};

export default new AuthService();
